import { useEffect } from "react"
import { Navigate, Outlet, Route, Routes } from "react-router-dom"

import "react-toastify/dist/ReactToastify.css"
import "@mdxeditor/editor/style.css"
import "assets/scss/globals.scss"

import { useAppDispatch, useAppSelector } from "store/hooks"
import { showTermsOfUseModal } from "store/modalSlise"

import { checkRoleType } from "utils/checkRoleType"
import { isRestrictedSubscriptionPlan } from "utils/isRestrictedSubscriptionPlan"
import { useLoadContractorAccountStatuses } from "hooks/useLoadContractorAccountStatuses"
import { Modals } from "modals"
import { AuthLayout } from "layouts/AuthLayout"
import MainLayout from "layouts/MainLayout/MainLayout"
import { NotFound } from "components/common/NotFound"
import { PrivateRoute } from "components/common/PrivateRoute"
import { ChangePassword } from "pages/common/Auth/ChangePassword"
import { CreatePassword } from "pages/common/Auth/CreatePassword"
import { ForgotPassword } from "pages/common/Auth/ForgotPassword"
import { SignIn } from "pages/common/Auth/SignIn"
import { RedirectPage } from "pages/common/RedirectPage"
import SampleErrorPage from "pages/common/SampleErrorPage"
import AlertsPage from "pages/contractor/AlertsPage/AlertsPage"
import ContractorDashboardPage from "pages/contractor/ContractorDashboardPage/ContractorDashboardPage"
import { ContractorUsers } from "pages/contractor/ContractorUsers/ContractorUsers"
import {
  CustomerDashboardAlerts,
  CustomerDashboardDetails,
  CustomerDashboardLayout,
  CustomerDashboardLiveData,
  CustomerDashboardMessages,
  CustomerDashboardMonitoring,
  CustomerDashboardOverview,
  CustomerDashboardPerformanceTest,
  CustomerDashboardReports,
} from "pages/contractor/CustomerDashboard"
import { Customers } from "pages/contractor/Customers"
import { MessageCenter } from "pages/contractor/MessageCenter"
import { MessageCenterOld } from "pages/contractor/MessageCenterOld"
import { MessageHistory } from "pages/contractor/MessageHistory"
import { MessageHistory as MessageHistoryOld } from "pages/contractor/MessageHistoryOld"
import { MessageReport } from "pages/contractor/MessageReport"
import { MessageReport as MessageReportOld } from "pages/contractor/MessageReportOld"
import { MessageTemplateAdd } from "pages/contractor/MessageTemplateAdd"
import { MessageTemplateAdd as MessageTemplateAddOld } from "pages/contractor/MessageTemplateAddOld"
import MessageTemplateContainerLayout from "pages/contractor/MessageTemplateContainer/MessageTemplateContainerLayout"
import MessageTemplateContainerLayoutOld from "pages/contractor/MessageTemplateContainer/MessageTemplateContainerLayoutOld"
import { MessageTemplateCreate } from "pages/contractor/MessageTemplateCreate"
import { MessageTemplateCreate as MessageTemplateCreateOld } from "pages/contractor/MessageTemplateCreateOld"
import { MessageTemplateEdit } from "pages/contractor/MessageTemplateEdit"
import { MessageTemplateEdit as MessageTemplateEditOld } from "pages/contractor/MessageTemplateEditOld"
import MonitoringPage from "pages/contractor/MonitoringPage/MonitoringPage"
import { NotificationsPage } from "pages/contractor/NotificationsPage"
import { Orders } from "pages/contractor/Orders"
import { OrdersOld } from "pages/contractor/OrdersOld"
import PerformanceTestHistoryPage from "pages/contractor/PerformanceTestHistoryPage/PerformanceTestHistoryPage"
import PerformanceTestPage from "pages/contractor/PerformanceTestPage/PerformanceTestPage"
import { Alerts as VendorAlerts } from "pages/vendor/Alerts"
import { Contractors } from "pages/vendor/Contractors"
import { Dashboard as VendorDashboard } from "pages/vendor/Dashboard"
import { Dashboard as VendorDashboardOld } from "pages/vendor/Dashboard/dashboardOld"
import { Devices as VendorDevices } from "pages/vendor/Devices"
import { MasterPass } from "pages/vendor/MasterPass"
import { Orders as VOrders } from "pages/vendor/Orders"
import { Users } from "pages/vendor/Users"
import { VOrdersOld } from "pages/vendor/VOrdersOld"
import { WarehouseList } from "pages/vendor/WarehouseList"
import { Devices as WHDevices } from "pages/warehouse/Devices"
import { Orders as WHOrders } from "pages/warehouse/Orders"
import { OrdersOld as WHOrdersOld } from "pages/warehouse/OrdersOld"

export const App = () => {
  const isUserAuthorized = useAppSelector(state => state.user.isUserAuthorized)
  const userProfile = useAppSelector(state => state.user.userProfile)
  const dispatch = useAppDispatch()

  useLoadContractorAccountStatuses()

  useEffect(() => {
    if (isUserAuthorized && userProfile?.terms !== true) {
      dispatch(showTermsOfUseModal(true))
    }
  }, [isUserAuthorized])

  return (
    <MainLayout>
      <Routes>
        <Route
          element={
            <>
              <Outlet />
              <Modals />
            </>
          }
        >
          <Route path="auth" element={userProfile ? <Navigate to="/" replace /> : <AuthLayout />}>
            <Route path="sign-in" element={<SignIn />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="create-password" element={<CreatePassword />} />
          </Route>

          {/* Sample Error Route for Sentry Testing */}
          <Route path="sample-error" element={<SampleErrorPage />} />

          {isRestrictedSubscriptionPlan(userProfile) && (
            <Route element={<PrivateRoute />}>
              <Route path="/" element={<MessageCenter hideUnavailableModal />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          )}

          {/* CONTRACTOR */}
          {["contractor-manager", "contractor-admin", "contractor-engineer"].includes(
            checkRoleType(userProfile?.type) || "",
          ) &&
            !isRestrictedSubscriptionPlan(userProfile) && (
              <Route element={<PrivateRoute />}>
                <Route path="dashboard" element={<ContractorDashboardPage />} />
                <Route path="order-legacy" element={<OrdersOld />}></Route>
                <Route path="orders" element={<Orders />}></Route>
                <Route path="customers" element={<Customers />} />
                <Route path="customers/:id/*" element={<CustomerDashboardLayout />}>
                  <Route index element={<CustomerDashboardOverview />} />
                  <Route path="overview" element={<CustomerDashboardOverview />} />
                  <Route path="details" element={<CustomerDashboardDetails />} />
                  <Route path="monitoring" element={<CustomerDashboardMonitoring />} />
                  <Route path="livedata" element={<CustomerDashboardLiveData />} />
                  <Route path="alerts" element={<CustomerDashboardAlerts />} />
                  <Route path="messages" element={<CustomerDashboardMessages />} />
                  <Route path="perf-test" element={<CustomerDashboardPerformanceTest />} />
                  <Route path="reports" element={<CustomerDashboardReports />} />
                </Route>
                <Route path="alerts" element={<AlertsPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="perf-test" element={<PerformanceTestPage />} />
                <Route path="perf-test-history" element={<PerformanceTestHistoryPage />} />
                <Route path="monitoring" element={<MonitoringPage />} />
                <Route path="messages" element={<Navigate to="center" replace />} />
                <Route path="master-pass" element={<MasterPass />} />
                <Route path="messages/*">
                  <Route path="center" element={<MessageCenterOld />} />
                  <Route path="history" element={<MessageHistoryOld />} />
                  <Route path="report" element={<MessageReportOld />} />
                  <Route element={<MessageTemplateContainerLayoutOld />}>
                    <Route path="create-template" element={<MessageTemplateCreateOld />} />
                    <Route path="edit-template" element={<MessageTemplateEditOld />} />
                    <Route path="add-template" element={<MessageTemplateAddOld />} />
                  </Route>
                </Route>
                <Route path="messages2/*">
                  <Route path="center" element={<MessageCenter />} />
                  <Route path="history" element={<MessageHistory />} />
                  <Route path="report" element={<MessageReport />} />
                  <Route element={<MessageTemplateContainerLayout />}>
                    <Route path="create-template" element={<MessageTemplateCreate />} />
                    <Route path="edit-template" element={<MessageTemplateEdit />} />
                    <Route path="add-template" element={<MessageTemplateAdd />} />
                  </Route>
                </Route>
              </Route>
            )}

          {["contractor-manager", "contractor-admin"].includes(checkRoleType(userProfile?.type) || "") &&
            !isRestrictedSubscriptionPlan(userProfile) && (
              <Route element={<PrivateRoute />}>
                <Route path="users" element={<ContractorUsers />} />
              </Route>
            )}

          {checkRoleType(userProfile?.type) === "contractor-technician" &&
            !isRestrictedSubscriptionPlan(userProfile) && (
              <Route element={<PrivateRoute />}>
                <Route path="customers/*" element={<Customers />} />
                <Route path="monitoring" element={<MonitoringPage />} />
                <Route path="alerts" element={<AlertsPage />} />
                <Route path="master-pass" element={<MasterPass />} />
                <Route path="notifications" element={<NotificationsPage />} />
              </Route>
            )}

          {/* VENDOR ADMIN */}
          {checkRoleType(userProfile?.type) === "vendor-admin" && (
            <Route element={<PrivateRoute />}>
              <Route path="contractors" element={<Contractors />} />
              <Route path="warehouse-list" element={<WarehouseList />} />
              <Route path="order-legacy" element={<VOrdersOld />}></Route>
              <Route path="orders" element={<VOrders />}></Route>
              <Route path="devices" element={<VendorDevices />} />
              <Route path="alerts" element={<VendorAlerts />} />
              <Route path="dashboard-legacy" element={<VendorDashboardOld />} />
              <Route path="dashboard" element={<VendorDashboard />} />
              <Route path="master-pass" element={<MasterPass />} />
              <Route path="admin/*">
                <Route path="users" element={<Users />} />
              </Route>
              <Route path="admin" element={<Navigate to="users" replace />} />
            </Route>
          )}

          {/* SALES MANAGER */}
          {checkRoleType(userProfile?.type) === "sales-manager" && (
            <Route element={<PrivateRoute />}>
              <Route path="contractors" element={<Contractors />} />
              <Route path="admin" element={<Navigate to="users" replace />} />
              <Route path="order-legacy" element={<VOrdersOld />}></Route>
              <Route path="orders" element={<VOrders />}></Route>
              <Route path="devices" element={<VendorDevices />} />
              <Route path="alerts" element={<VendorAlerts />} />
              <Route path="dashboard-legacy" element={<VendorDashboardOld />} />
              <Route path="dashboard" element={<VendorDashboard />} />
              <Route path="master-pass" element={<MasterPass />} />
            </Route>
          )}

          {/* WAREHOUSE MANAGER */}
          {checkRoleType(userProfile?.type) === "warehouse-manager" && (
            <Route element={<PrivateRoute />}>
              <Route path="wh-orders-legacy" element={<WHOrdersOld />} />
              <Route path="wh-orders" element={<WHOrders />} />
              <Route path="wh-devices" element={<WHDevices />} />
            </Route>
          )}
          <Route element={<PrivateRoute />}>
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/" element={<RedirectPage />} />
          <Route path="*" element={<RedirectPage />} />
        </Route>
      </Routes>
    </MainLayout>
  )
}
